<template>
  <div class="drawer">
    <div class="drawer-container">
      <div class="drawer-content with-footer">
        <el-form
          ref="purchaseForm"
          :model="purchase"
          label-width="80px"
          size="small"
          class="box"
        >
          <fieldset class="mgb15">
            <legend>
              {{ $t("text.baseInfo") }}
              <span class="float-right">
                {{ $t("order.hurry") }}
                <el-checkbox v-model="purchase.hurry" />
              </span>
            </legend>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item
                  :label="$t('purchase.serialNo')"
                  :prop="'serialNo'"
                  :rules="[
                    {
                      required: true,
                      validator: validateSerialNo,
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.serialNo"
                    :disabled="!(purchase.workflowState == 'newly')"
                    clearable
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('menu.suppliers')"
                  :prop="'supplierId'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('menu.suppliers')]),
                      trigger: 'blur',
                    },
                  ]"
                  >{{ purchase.supplierName }}</el-form-item
                >
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('text.handOverAt')"
                  :prop="'handOverAt'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('text.handOverAt')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-date-picker
                    size="small"
                    v-model="purchase.handOverAt"
                    type="date"
                    style="width: 100%"
                    :picker-options="pickerTodayAfterTime"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
          <fieldset class="mgb15">
            <legend>{{ $t("order.productInfo") }}</legend>
            <el-row>
              <el-col :span="16">
                <el-row>
                  <el-col :span="12">
                    <el-form-item
                      :label="$t('product.productNo')"
                      :prop="'productNo'"
                      :rules="[
                        {
                          required: true,
                          message: $t('rules.required', [
                            $t('product.productNo'),
                          ]),
                          trigger: 'blur',
                        },
                      ]"
                    >
                      <el-autocomplete
                        v-model="purchase.productNo"
                        :disabled="
                          !(
                            purchase.workflowState == 'newly' && purchase.follow
                          )
                        "
                        :fetch-suggestions="querySearchProductNo"
                        :trigger-on-focus="false"
                        style="width: 100%"
                        @select="handleSelectProductNo"
                      >
                        <template slot-scope="{ item }">
                          <div class="name">{{ item.productNo }}</div>
                          <span class="addr">{{ item.productNoAlias }}</span>
                        </template>
                      </el-autocomplete>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item
                      :label="$t('product.category')"
                      :prop="'categoryId'"
                    >
                      <el-select
                        size="small"
                        v-model="purchase.categoryId"
                        clearable
                        filterable
                        @change="selsectCategory"
                      >
                        <el-option
                          v-for="c in categories"
                          :key="c.id"
                          :label="c.name"
                          :value="c.id"
                        />
                      </el-select>
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row>
                  <el-col :span="12">
                    <el-form-item :label="$t('product.size')" :prop="'size'">
                      <el-autocomplete
                        :disabled="!purchase.follow"
                        size="small"
                        v-model.trim="purchase.size"
                        :fetch-suggestions="querySearchSize"
                        style="width: 100%"
                        @select="handleSelectSize"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$t('product.color')" :prop="'color'">
                      <el-autocomplete
                        :disabled="!purchase.follow"
                        size="small"
                        v-model.trim="purchase.color"
                        :fetch-suggestions="querySearchColor"
                        style="width: 100%"
                        @select="handleSelectColor"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.colorImage')"
                  :prop="'colorImage'"
                >
                  <upload-single-picture v-model="purchase.colorImage" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="8">
                <el-form-item
                  :label="$t('purchase.price')"
                  :prop="'price'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('purchase.price')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.price"
                    :disabled="
                      !['newly', 'gived'].includes(purchase.workflowState)
                    "
                  />
                </el-form-item>
              </el-col>

              <el-col :span="8">
                <el-form-item
                  :label="$t('text.quantity')"
                  :prop="'quantity'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('text.quantity')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-row :gutter="2">
                    <el-col :span="14">
                      <el-input
                        v-model="purchase.quantity"
                        :disabled="!(purchase.workflowState == 'newly')"
                      />
                    </el-col>
                    <el-col :span="9">
                      <el-input
                        v-if="!(purchase.workflowState == 'newly')"
                        v-model="purchase.quantity"
                        :disabled="!(purchase.workflowState == 'newly')"
                      />
                      <el-select v-else v-model="purchase.unit">
                        <el-option
                          v-for="u in units"
                          :key="u"
                          :label="u"
                          :value="u"
                        />
                      </el-select>
                    </el-col>
                  </el-row>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
          <fieldset v-if="productNoAttributes.length != 0" class="mgb15">
            <legend>
              {{ $t("product.specJson") }}
              <el-button
                v-if="productNoAttributes.length != 0"
                type="warning"
                size="mini"
                @click="clearSpecJson"
              >
                {{ $t("action.clearSpecJson") }}
              </el-button>
            </legend>
            <el-row :gutter="20" class="mgb10">
              <el-col
                v-for="attribute in productNoAttributes"
                :key="attribute.name"
                :span="6"
                class="mgb10"
              >
                <el-input size="small" v-model="attribute.value">
                  <template slot="prepend">
                    <div>
                      <raw-html-limit-number
                        :content="attribute.name"
                        :number="6"
                      />
                    </div>
                  </template>
                </el-input>
              </el-col>
            </el-row>
          </fieldset>
          <el-row :gutter="20">
            <el-col :span="16">
              <fieldset class="mgb15">
                <legend>
                  {{ $t("product.images") }}
                  <svg-icon icon-class="link" @click="linkImage" />
                </legend>
                <dropzone v-model="purchase.images" multiple />
                <!-- :delete="
                    !['accepted', 'holding'].includes(purchase.workflowState)
                  " -->
              </fieldset>
            </el-col>
            <el-col :span="8">
              <fieldset class="mgb15">
                <legend>{{ $t("product.attachment") }}</legend>
                <div class="mgb10">
                  <small class="orange">{{
                    $t("product.attachmentExplain")
                  }}</small>
                </div>
                <attachment v-model="purchase.attachment" />
              </fieldset>
            </el-col>
          </el-row>
          <fieldset class="mgb15">
            <legend>{{ $t("text.remark") }}</legend>
            <tinymce-editor
              ref="editor"
              v-model="purchase.remark"
              :height="300"
            ></tinymce-editor>
          </fieldset>
          <fieldset v-if="!Permit.isFreeEdition()" class="mgb15">
            <legend>{{ $t("purchase.receiveAdress") }}</legend>
            <el-row>
              <el-col :span="8">
                <el-form-item :label="$t('user.name')" :prop="'customerName'">
                  <el-input size="small" v-model.trim="purchase.customerName" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('courier.phone')"
                  :prop="'customerPhone'"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.customerPhone"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('tenant.country')"
                  :prop="'customerCountry'"
                >
                  <el-select
                    size="small"
                    v-model.trim="purchase.customerCountry"
                    clearable
                    filterable
                  >
                    <el-option
                      v-for="country in countries"
                      :key="country"
                      :label="$t('countries.' + country)"
                      :value="country"
                    />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="8">
                <el-form-item
                  :label="$t('tenant.province')"
                  :prop="'customerProvince'"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.customerProvince"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item :label="$t('tenant.city')" :prop="'customerCity'">
                  <el-input size="small" v-model.trim="purchase.customerCity" />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('tenant.address')"
                  :prop="'customerAddress'"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.customerAddress"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="8">
                <el-form-item
                  :label="$t('customer.zipcode')"
                  :prop="'customerZipcode'"
                >
                  <el-input
                    size="small"
                    v-model.trim="purchase.customerZipcode"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
        </el-form>
      </div>
    </div>
    <div class="drawer-footer">
      <el-button
        type="primary"
        size="small"
        plain
        :loading="saveDoing"
        @click="saveSubmit"
        >{{ $t("text.submit") }}</el-button
      >
    </div>
    <el-dialog
      :append-to-body="true"
      :title="$t('action.linkImage')"
      :visible.sync="linkImageDF"
      :close-on-click-modal="false"
      width="50%"
    >
      <el-form ref="linkImageForm" :model="linkImageObj">
        <el-form-item
          :prop="'linkImageStr'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('action.linkImage')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            v-model="linkImageObj.linkImageStr"
            :placeholder="$t('prompt.linkImageExplain')"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 10 }"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="linkImageDoing"
          size="small"
          @click="linkImageSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="linkImageDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>

    <!-- 是否发消息提示 -->
    <el-dialog
      :append-to-body="true"
      :title="'提示'"
      :close-on-click-modal="false"
      :visible.sync="sendNoticeDF"
      width="30%"
    >
      <el-form ref="sendNoticeForm">
        <el-form-item prop="notice">
          <el-checkbox v-model="notice">是否发送通知</el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="sendNoticeDoing"
          size="small"
          @click="saveSubmitFunction"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="sendNoticeDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import axios from "axios";
import Vue from "vue";
import ls from "store2";
import { page } from "@/utils/mixins.js";
import { countries } from "@/utils/enums.js";
import Util from "@/utils/index.js";
import UploadSinglePicture from "@/components/UploadSinglePicture";
import Dropzone from "@/components/Dropzone";
import Attachment from "@/components/Attachment";
import TinymceEditor from "@/components/TinymceEditor";

//
import Purchase from "@/api/purchase.js";
import Category from "@/api/category.js";
import Size from "@/api/size.js";
import Enumeration from "@/api/enumeration.js";
import Color from "@/api/color.js";
import Product from "@/api/product.js";
import Supplier from "@/api/supplier.js";

export default {
  props: {
    id: Number,
  },
  mixins: [page],
  components: {
    UploadSinglePicture,
    Dropzone,
    Attachment,
    TinymceEditor,
  },
  data() {
    var validateSerialNo = (rule, value, callback) => {
      const self = this;
      if (value === null || value === "") {
        callback(
          new Error(self.$t("rules.required", [self.$t("purchase.serialNo")]))
        );
        return;
      }
      if (!Util.regexAlphabetNumberLine(value)) {
        callback(new Error(self.$t("prompt.regexAlphabetNumberLine")));
        return;
      }
      Purchase.findBySerialNo({ serialNo: value }).then(function (res) {
        if (res.data.code === 200 && res.data.entity.id !== self.purchase.id) {
          callback(
            new Error(self.$t("rules.exist", [self.$t("purchase.serialNo")]))
          );
        } else {
          callback();
        }
      });
    };
    return {
      pickerTodayAfterTime: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      validateSerialNo: validateSerialNo,
      countries: countries,
      purchase: { hurry: null },
      suppliers: [],
      categories: [],
      sizes: [],
      units: [],
      colors: [],
      attributes: [],

      products: [],
      productNoColors: [],
      productNoSizes: [],
      productNoAttributes: [],

      productSuppliers: [],
      categorySizes: [],
      // 图片链接
      linkImageDF: false,
      linkImageObj: {
        linkImageStr: null,
      },
      linkImageDoing: false,

      saveDoing: false,
      //
      sendNoticeDF: false,
      notice: true,
      sendNoticeDoing: false,
    };
  },

  computed: {},
  created() {
    const self = this;
    self.loadingStart();
    axios
      .all([
        Purchase.findById(self.id),
        Supplier.findByTenantId(),
        Category.findByTenantId(),
        Size.findByTenantId(),
        Enumeration.findByType("unit"),
        Color.findByTenantId(),
        Enumeration.findByType("attribute"),
      ])
      .then(
        axios.spread(function (
          purchase,
          suppliers,
          categories,
          sizes,
          units,
          colors,
          attributes
        ) {
          if (purchase.data.code === 200) {
            self.purchase = purchase.data.entity;
            self.initData();
          } else {
            self.sysError();
          }
          if (suppliers.status === 200) {
            self.suppliers = suppliers.data;
          } else {
            self.sysError();
          }
          if (categories.status === 200) {
            self.categories = categories.data;
          } else {
            self.sysError();
          }
          if (sizes.status === 200) {
            self.sizes = sizes.data;
          } else {
            self.sysError();
          }
          if (units.data.code === 200) {
            self.units = JSON.parse(units.data.entity.itemsJson);
          } else {
            self.sysError();
          }
          if (colors.status === 200) {
            self.colors = colors.data;
          } else {
            self.sysError();
          }
          if (attributes.data.code === 200) {
            self.attributes = JSON.parse(attributes.data.entity.itemsJson);
          } else {
            self.sysError();
          }
          self.loadingEnd();
        })
      );
  },
  methods: {
    clearSpecJson() {
      if (this.productNoAttributes.length != 0) {
        for (const productNoAttribute of this.productNoAttributes) {
          productNoAttribute.value = null;
        }
      }
    },
    initData() {
      const self = this;
      self.selsectCategory();
      Product.findByProductNo({ productNo: self.purchase.productNo }).then(
        function (res) {
          if (res.data.code === 200) {
            self.productNoColors =
              res.data.entity.colors.length !== 0
                ? res.data.entity.colors
                : self.colors.map((c) => c.name);
            self.productNoSizes =
              res.data.entity.sizes.length !== 0
                ? res.data.entity.sizes
                : self.sizes.map((s) => s.name);
            self.productNoAttributes =
              JSON.parse(res.data.entity.customAttributeJson).length !== 0
                ? JSON.parse(res.data.entity.customAttributeJson).map((att) => {
                    return { name: att, value: null };
                  })
                : self.attributes.map((att) => {
                    return { name: att, value: null };
                  });
          } else {
            self.productNoColors = self.colors.map((c) => c.name);
            self.productNoSizes = self.sizes.map((s) => s.name);
            self.productNoAttributes = self.attributes.map((att) => {
              return { name: att, value: null };
            });
          }
          const purchaseSpecJsonArrs = JSON.parse(
            self.purchase.specJson || "[]"
          );

          if (purchaseSpecJsonArrs.length !== 0) {
            for (const purchaseSpecJsonArr of purchaseSpecJsonArrs) {
              if (
                self.productNoAttributes.find(
                  (productAtt) => productAtt.name === purchaseSpecJsonArr.name
                ) == null
              ) {
                self.productNoAttributes.push({
                  name: purchaseSpecJsonArr.name,
                  value: purchaseSpecJsonArr.value,
                });
              } else {
                const index = self.getIndexOf(self.productNoAttributes, {
                  name: purchaseSpecJsonArr.name,
                  value: null,
                });
                self.productNoAttributes[index].value =
                  purchaseSpecJsonArr.value;
              }
            }
          }
        }
      );
    },
    //
    selsectCategory() {
      const self = this;
      if (self.purchase.categoryId === "") {
        self.purchase.categoryId = null;
      }
      if (self.purchase.categoryId != null && self.purchase.categoryId !== "") {
        const selsectCategoryObj = self.categories.find(
          (c) => c.id === self.purchase.categoryId
        );
        if (
          selsectCategoryObj &&
          selsectCategoryObj.sizes != null &&
          selsectCategoryObj.sizes.length !== 0
        ) {
          self.categorySizes = selsectCategoryObj.sizes;
        } else {
          self.categorySizes = [];
        }
      } else {
        self.categorySizes = [];
      }
    },
    getIndexOf(arrs, obj) {
      for (const i in arrs) {
        if (arrs[i].name === obj.name) {
          return i;
        }
      }
    },

    // 图片链接
    linkImage() {
      this.linkImageDF = true;
      this.linkImageObj.linkImageStr = null;
    },
    linkImageSubmit() {
      const self = this;
      this.$refs.linkImageForm.validate((valid) => {
        if (valid) {
          const linkImageArr = self.linkImageObj.linkImageStr.split("\n");
          const linkImageArrFilter = linkImageArr.filter((a) => a != null);
          if (self.purchase.images == null) {
            self.purchase.images = linkImageArrFilter.join(";");
          } else {
            self.purchase.images =
              self.purchase.images + ";" + linkImageArrFilter.join(";");
          }
          this.linkImageDF = false;
        } else {
          return false;
        }
      });
    },

    querySearchProductNo(queryString, cb) {
      const self = this;
      self.productNoColors = self.colors.map((c) => c.name);
      self.productNoSizes = self.sizes.map((s) => s.name);
      self.productNoAttributes = self.attributes.map((att) => {
        return { value: null, name: att };
      });
      if (queryString == null || queryString.length < 1) {
        return;
      }
      Product.vagueFindByProductNo({ productNo: queryString }).then(function (
        res
      ) {
        self.products = res.data;
        if (self.products.length !== 0) {
          for (const p of self.products) {
            Vue.set(p, "value", p.productNo);
          }
        }
        cb(self.products);
      });
    },
    handleSelectProductNo(item) {
      const self = this;
      self.purchase.productNo = item.productNo;
      self.purchase.categoryId = item.categoryId;
      self.purchase.categoryName = item.categoryName;
      self.selsectCategory();
      self.purchase.color = null;
      self.purchase.colorImage = null;
      self.purchase.size = null;
      self.purchase.unit = item.unit;
      self.purchase.images = item.images;
      self.purchase.craftwork = item.craftwork;
      self.productNoColors = item.colors;
      self.productNoSizes = item.sizes;
      self.productNoAttributes = JSON.parse(
        item.customAttributeJson || "[]"
      ).map((att) => {
        return { value: null, name: att };
      });
    },
    // 颜色
    querySearchColor(queryString, cb) {
      this.purchase.colorImage = null;
      const colors =
        this.productNoColors.length !== 0
          ? this.productNoColors.map((pc) => {
              return { value: pc };
            })
          : this.colors.map((c) => {
              return { value: c.name };
            });

      const filterColors = colors.filter(
        (c) => c.value.indexOf(queryString) !== -1
      );
      if (queryString == null) {
        cb(colors);
      } else {
        cb(filterColors);
      }
    },
    handleSelectColor() {
      this.purchase.color = this.purchase.color;
      //
      const colorObj = this.colors.find((c) => c.name === this.purchase.color);
      if (colorObj != null && colorObj.image != null) {
        this.purchase.colorImage = colorObj.image;
      } else {
        this.purchase.colorImage = null;
      }
    },
    // 规格
    querySearchSize(queryString, cb) {
      let sizes = [];
      if (this.productNoSizes.length !== 0) {
        sizes = this.productNoSizes.map((pc) => {
          return { value: pc };
        });
      } else {
        if (self.categorySizes.length !== 0) {
          sizes = this.categorySizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        } else {
          sizes = this.sizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        }
      }
      const filterSizes = sizes.filter(
        (c) => c.value.indexOf(queryString) !== -1
      );

      if (queryString == null) {
        cb(sizes);
      } else {
        cb(filterSizes);
      }
    },
    handleSelectSize() {
      const self = this;
      this.purchase.size = this.purchase.size;
      this.purchase.remark = this.purchase.remark.replace(/\【.*?\】/g, "");
      let obj = this.sizes.find((s) => s.name === this.purchase.size);
      if (obj && obj.explain != null) {
        this.purchase.remark = "【" + obj.explain + "】" + this.purchase.remark;
      }
    },

    saveSubmit() {
      const self = this;
      this.$refs.purchaseForm.validate((valid) => {
        if (valid) {
          if (!["newly", "gived"].includes(self.purchase.workflowState)) {
            self.sendNoticeDF = true;
            self.notice = true;
          } else {
            self.purchase.notice = false;
            self.saveSubmitFunction();
          }
        } else {
          return false;
        }
      });
    },

    saveSubmitFunction() {
      const self = this;
      self.sendNoticeDoing = true;
      if (!["newly", "gived"].includes(self.purchase.workflowState)) {
        self.purchase.notice = self.notice;
      }

      self.purchase.handOverAt = Util.formatDate(self.purchase.handOverAt);

      self.purchase.categoryName =
        self.categories.find((s) => s.id === self.purchase.categoryId) != null
          ? (self.purchase.categoryName = self.categories.find(
              (s) => s.id === self.purchase.categoryId
            ).name)
          : "";
      const attributesArr =
        self.productNoAttributes.length !== 0
          ? self.productNoAttributes.filter((sp) => sp.value != null)
          : [];
      self.purchase.specJson = JSON.stringify(attributesArr);
      Purchase.update(self.purchase.id, self.purchase).then(function (res) {
        self.sendNoticeDoing = false;
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.editSuccess"),
            type: "success",
          });
          self.sendNoticeDF = false;
          self.$emit("updateInfo", self.purchase);
        } else {
          self.sysError();
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>